/**
 * Die Karte am einzelnen Tauchgang: wo abgetaucht, wo aufgetaucht wurde.
 *
 * Die Weltkarte in `views/karte.js` beantwortet „wo war ich überall", diese
 * hier „wo genau war das". Deshalb eine eigene, kleine Leaflet-Instanz statt
 * eines Schalters in der grossen: Dort wird gruppiert und im Umkreis gesucht,
 * hier gibt es zwei Punkte und eine Linie dazwischen.
 *
 * **Zwei Kartengründe.** Über Riffen sagt ein Luftbild mehr als jede Karte —
 * man sieht die Riffkante, an der man entlanggetaucht ist. Über Schweizer Seen
 * ist die Karte lesbarer. Deshalb umschaltbar, und die Wahl bleibt stehen
 * (`localStorage`), weil niemand sie bei jedem Tauchgang neu treffen will.
 *
 * Ohne Netz bleibt der Grund leer und die Marker liegen trotzdem richtig —
 * dasselbe Verhalten wie auf der Weltkarte.
 */

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { entfernung } from '../geo.js';
import { koordinaten } from '../format.js';
import { istGrob } from '../mobil.js';
import { grundwahl, grundwahlLeiste, haltGroesseNach } from '../kartengrund.js';
import { tauchpunkte } from '../orte.js';
import { tiefenfarbe } from '../tiefenfarbe.js';

const ZOOM_EINZELN = 15;

/** Ein- und Auftauchpunkt fallen bei ruhigem Wasser zusammen — dann keine Linie. */
function strecke(punkte) {
  if (punkte.length < 2) return null;
  const km = entfernung(punkte[0], punkte[1]);
  return km < 0.02 ? null : km;
}

const weg = (km) => (km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`);

function beschriftung(punkt, km) {
  if (punkt.art === 'platz') return punkt.name;
  if (punkt.art === 'eintauchen') return 'Eintauchen';
  return km ? `Auftauchen · ${weg(km)}` : 'Auftauchen';
}

/**
 * Baut den Kartenabschnitt. Zurück kommt das Element mit `aufraeumen()` daran —
 * Leaflet lässt sonst Ereignishorcher am Fenster zurück.
 */
export function ansichtDetailkarte(tg, platz) {
  const punkte = tauchpunkte(tg, platz);
  if (punkte.length === 0) return null;

  const wurzel = document.createElement('section');
  wurzel.className = 'block-weit tauchkarte-block';
  const km = strecke(punkte);

  wurzel.innerHTML = `
    <div class="tauchkarte-kopf">
      <h2>Position</h2>
      ${grundwahlLeiste()}
    </div>
    <div class="tauchkarte"></div>
    <p class="tauchkarte-fuss"></p>`;

  const fuss = [
    punkte.map((p) => `${beschriftung(p, null)}: ${koordinaten(p.lat, p.lon)}`).join(' · '),
    km ? `Versatz ${weg(km)}` : null,
    punkte[0].art === 'platz' ? 'Position des Tauchplatzes — der Tauchgang hat kein GPS' : null,
  ].filter(Boolean).join(' · ');
  wurzel.querySelector('.tauchkarte-fuss').textContent = fuss;

  let karte = null;
  let loeseGroesse = null;
  const grund = grundwahl(wurzel.querySelector('.grundwahl'));

  // Leaflet braucht ein Element, das schon im Dokument haengt und eine Hoehe hat.
  requestAnimationFrame(() => {
    const behaelter = wurzel.querySelector('.tauchkarte');
    // Am Finger zieht ein Wisch ueber der Karte sie mit, statt die Seite zu
    // scrollen — mitten in einer Seite ist eine ziehbare Karte eine Falle.
    // Auf der Detailseite ist sie Abbildung, nicht Arbeitsflaeche: Wer sie
    // bewegen will, geht auf den Kartenreiter. Am Zeiger bleibt alles.
    karte = L.map(behaelter, {
      scrollWheelZoom: false,
      dragging: !istGrob(),
      tap: false,
      attributionControl: false,
    });
    L.control.attribution({ prefix: false }).addTo(karte);
    // Zwei Punkte werden eingepasst und haben damit dasselbe Problem wie die
    // Weltkarte; ein einzelner bekommt eine feste Stufe und ist von der Groesse
    // des Behaelters unabhaengig.
    const ausschnitt = punkte.length > 1
      ? L.latLngBounds(punkte.map((punkt) => [punkt.lat, punkt.lon]))
      : null;
    loeseGroesse = haltGroesseNach(karte, behaelter, ausschnitt,
      { padding: [40, 40], maxZoom: 17 });
    grund.anKarte(karte);

    if (km) {
      L.polyline(punkte.map((p) => [p.lat, p.lon]), {
        color: tiefenfarbe(tg?.max_depth_m), weight: 2, opacity: 0.8, dashArray: '5 5',
      }).addTo(karte);
    }
    for (const punkt of punkte) {
      const start = punkt.art !== 'auftauchen';
      L.circleMarker([punkt.lat, punkt.lon], {
        radius: 7,
        color: tiefenfarbe(0),
        weight: 2,
        fillColor: tiefenfarbe(0),
        fillOpacity: start ? 0.85 : 0.12,   // voll rein, hohl raus
      }).bindTooltip(beschriftung(punkt, km), { direction: 'top', permanent: punkte.length > 1 })
        .addTo(karte);
    }

    if (punkte.length > 1) {
      karte.fitBounds(L.latLngBounds(punkte.map((p) => [p.lat, p.lon])),
        { padding: [40, 40], maxZoom: 17 });
    } else {
      karte.setView([punkte[0].lat, punkte[0].lon], ZOOM_EINZELN);
    }
  });

  wurzel.aufraeumen = () => {
    loeseGroesse?.();
    karte?.remove();
    karte = null;
  };
  return wurzel;
}
